import { Component, OnInit, OnDestroy } from '@angular/core';
import { copyToClipboard, momentCustomInstance as moment } from '@projects/shared/src/app/utils/helper';
import { NavService } from '@projects/shared/src/app/services/nav.service';
import { Subject, timer, Subscription, of } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { ToastService } from '@shared/services/toast/toast.service';
import { AuthenticationService } from '@app/core';

@Component({
  selector: 'app-today-scheduled-callbacks',
  templateUrl: './today-scheduled-callbacks.component.html',
})
export class TodayScheduledCallbacksComponent implements OnInit, OnDestroy {
  moment = moment;
  copyToClipboard = copyToClipboard;
  public callBannerState = {
    displayText: '',
    activeDueCall: false,
    waitTimeMins: 0,
    currentCallId: '',
    tooltipText: '',
  };

  private destroy$ = new Subject<void>();
  private notifiedItems = new Set<string>();
  private timerSub?: Subscription;
  private readonly MISSED_NOTIFICATION_GRACE_MINUTES = 5;
  constructor(
    public navService: NavService,
    public toastService: ToastService,
    public authService: AuthenticationService,
  ) { }

  ngOnInit(): void {
    this.getScheduleCallBack();
    this.navService.todayScheduledCallbacks.isReload$.asObservable().pipe(takeUntil(this.destroy$))
      .subscribe((reload: boolean) => {
        console.log(" Reloading scheduled callbacks due to external trigger:", reload);
        if (reload) {
          this.getScheduleCallBack();
        }
      })
  }

  ngOnDestroy(): void {
    if (this.timerSub) {
      this.timerSub.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
  
  /**
   * Fetches scheduled callback times from API
   */
  public getScheduleCallBack(): void {
    // this.callBannerState.scheduledCallCount = 10;
    // this.apiData = Array.from({ length: 5 }, (_, index) => ({
    //   id: Math.random().toString(36).substring(2, 10),
    //   time: moment().add((index + 1) * 5, "minutes").format(),
    // }));
    // if (this.apiData.length > 0) {
    //   console.log('Call scheduled',this.apiData);
    //   console.log('Current time',moment().format());
    //   this.startScheduleMonitoring();
    // }
    // return
    this.navService.getScheduleCallBack()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.navService.todayScheduledCallbacks.scheduledCallCount = res.TotalCount;
            this.navService.todayScheduledCallbacks.apiItems = res.Results;
            const now = moment();
            this.navService.todayScheduledCallbacks.localItems = res.Results.map((item: any, index: number) => ({
              id: item.call_sid,
              time: item.callback_info.time
            })).filter((item: any) => {
              const scheduledMoment = moment(item.time);
              return scheduledMoment.diff(now, 'minutes', true) >= -this.MISSED_NOTIFICATION_GRACE_MINUTES;
            });
            if (this.navService.todayScheduledCallbacks.localItems.length > 0) {
              this.startScheduleMonitoring();
            } else {
              this.stopScheduleMonitoring();
              this.resetBannerState();
            }
          } else {
            this.stopScheduleMonitoring();
            this.resetBannerState();
          }
        },
        error: (err) => {
          console.error('Failed to load schedule callbacks:', err);
          this.toastService.error('Failed to refresh schedule');
          this.stopScheduleMonitoring();
          this.resetBannerState();
        }
      });
  }
  
  private stopScheduleMonitoring(): void {
    if (this.timerSub) {
      this.timerSub.unsubscribe();
      this.timerSub = undefined;
    }
  }

  private startScheduleMonitoring(): void {
    // Unsubscribe existing timer to prevent parallel timers running
    if (this.timerSub) {
      this.timerSub.unsubscribe();
    }

    this.timerSub = timer(0, 10000)
      .pipe(
        takeUntil(this.destroy$),
        catchError((err) => {
          console.error('Schedule monitoring stream error:', err);
          return of(null);
        })
      )
      .subscribe(() => {
        try {
          this.checkUpcomingCalls();
        } catch (error) {
          console.error('Error executing checkUpcomingCalls:', error);
        }
      });
  }

  private checkUpcomingCalls(): void {
    // Fix: Reset banner state if no data left so activeDueCall doesn't stay true forever
    if (this.navService.todayScheduledCallbacks.localItems.length === 0) {
      this.resetBannerState();
      return;
    }

    const now = moment();
    const callsDueNow: any[] = [];
    const callsDueSoon: any[] = [];
    const itemsToKeep: any[] = [];

    this.navService.todayScheduledCallbacks.localItems.forEach((item: any) => {
      const scheduledMoment = moment(item.time);
      const remainingMinutes = scheduledMoment.diff(now, 'minutes', true);

      const key15Min = `${item.id}_${item.time}_15MIN`;
      const keyNow = `${item.id}_${item.time}_NOW`;

      // 1. Alert for 15 Mins
      if (remainingMinutes <= 15 && remainingMinutes > 0.5) {
        if (!this.notifiedItems.has(key15Min)) {
          const displayMins = Math.ceil(remainingMinutes);
          const msg = `Call scheduled(${item.id}) at ${moment(item.time).format('LT')} is due in ~${displayMins} mins!`;
          this.toastService.info(msg);
          this.notifiedItems.add(key15Min);
        }
        callsDueSoon.push({ item, remainingMinutes });
        itemsToKeep.push(item);
      }
      // 2. Exact Due Time Window ([-0.5, 0.5] mins)
      else if (remainingMinutes <= 0.5 && remainingMinutes >= -0.5) {
        if (!this.notifiedItems.has(keyNow)) {
          const msg = `Call scheduled(${item.id}) at ${moment(item.time).format('LT')} is due NOW!`;
          this.toastService.info(msg);
          this.notifiedItems.add(keyNow);
        }
        callsDueNow.push(item);
        itemsToKeep.push(item);
      }
      // 3. Missed Execution Handling
      else if (remainingMinutes < -0.5) {
        if (remainingMinutes >= -this.MISSED_NOTIFICATION_GRACE_MINUTES) {
          if (!this.notifiedItems.has(keyNow)) {
            const msg = `MISSED: Call scheduled at ${moment(item.time).format('LT')} was due a few minutes ago!`;
            this.toastService.warning(msg);
            this.notifiedItems.add(keyNow);
          }
        }
        // Expired items are excluded from itemsToKeep
      }
      // 4. Future calls (> 15 mins away)
      else {
        itemsToKeep.push(item);
      }
    });

    this.navService.todayScheduledCallbacks.localItems = itemsToKeep;
    this.updateBannerState(callsDueNow, callsDueSoon);
  }

  private updateBannerState(dueNow: any[], dueSoon: any[]): void {
    if (this.navService.todayScheduledCallbacks.localItems.length === 0) {
      this.resetBannerState();
      return;
    }

    if (dueNow.length > 0) {
      const ids = dueNow.map((c) => c.id);
      const { displayIds, tooltipText } = this.formatCallIds(ids);

      this.callBannerState.displayText = `Call scheduled Now (${displayIds})`;
      this.callBannerState.tooltipText = tooltipText;
      this.callBannerState.waitTimeMins = 0;
      this.callBannerState.activeDueCall = true;
      this.callBannerState.currentCallId = ids.join(',');
    } else if (dueSoon.length > 0) {
      dueSoon.sort((a, b) => a.remainingMinutes - b.remainingMinutes);
      const closestMins = Math.ceil(dueSoon[0].remainingMinutes);

      const closestCalls = dueSoon.filter(
        (c) => Math.ceil(c.remainingMinutes) === closestMins
      );
      const ids = closestCalls.map((c) => c.item.id);

      const { displayIds, tooltipText } = this.formatCallIds(ids);

      this.callBannerState.displayText = `Call Wait Time (${displayIds})`;
      this.callBannerState.tooltipText = tooltipText;
      this.callBannerState.waitTimeMins = closestMins;
      this.callBannerState.activeDueCall = false;
      this.callBannerState.currentCallId = ids.join(',');
    } else {
      this.resetBannerState();
    }
  }

  private formatCallIds(ids: string[]): { displayIds: string; tooltipText: string } {
    if (!ids || ids.length === 0) return { displayIds: '', tooltipText: '' };

    const tooltipText = ids.join(', ');
    let firstId = ids[0];

    // Truncate ID if longer than 12 chars
    if (firstId.length > 12) {
      firstId = `${firstId.substring(0, 6)}...${firstId.substring(firstId.length - 4)}`;
    }

    let displayIds = firstId;
    if (ids.length > 1) {
      displayIds += ` (+${ids.length - 1} more)`;
    }

    return { displayIds, tooltipText };
  }

  private resetBannerState(): void {
    this.callBannerState.displayText = '';
    this.callBannerState.activeDueCall = false;
    this.callBannerState.waitTimeMins = 0;
    this.callBannerState.currentCallId = '';
  }

}





// const payload = {
//    id:'',
//    call_sid: object1.CallSid,
//    tfn:"->need this from code",
//    disposition: {
//     "name": object1.Disposition.Name
//    },
//    customer_info:{
//     "first_name": object1.CustomerInfo.FirstName,
//      "last_name": object1.CustomerInfo.LastName
//    },
// "callback_info": {
//                 "time": object1.CallbackTime to utc,
//                 "is_completed": "No"
//             },

//             "CreatedOn": "",
//             "Duration": 0,
//             "direction": "->need this from code",
//             "CallbackNumber": object1.ConfirmCallbackNumber,
//             "Campaign": "->need this from code but how",
//             "CampaignCode": "->need this from code",
//             "CampaignId": "->need this from code but how",
//             "TFNCampaign": "->need this from code",
//             "TFNCampaignCode": "TS1",
//             "TFNCampaignId": "->need this from code but how",
//             "LanguageCode": null,
//             "ConfirmBTN": object1.ConfirmCallbackNumber,
//             "LeadId": null,
//             "CallSid": object1.CallSid,
//             "Direction": "->need this from code",
//             "Disposition": object1.Disposition.Name,
//             "TFN": "->need this from code"
// }
// const object1 = {
//     "LeadId": "69ea049f4422f4e6c5916a56",
//     "CallSid": "TS12026073005100900010",
//     "UserId": "698cc7eaa81c6bcbfd7e497a",
//     "Disposition": {
//         "Id": "66916a2ae582e87893b6b701",
//         "Name": "Call Back Scheduled ",
//         "GroupName": "CallBack",
//         "TermCode": "CB"
//     },
//     "CallbackTime": "2026-07-30T05:20:00-04:00",
//     "CustomerInfo": {
//         "FirstName": "ff",
//         "LastName": "rr",
//         "Address": "10000 Omni Dr",
//         "Apartment": "",
//         "City": "Jacksonville",
//         "State": "FL",
//         "Zip": "32222",
//         "Email": "a@g.com",
//         "IsWorkbench": false,
//         "CustomerType": "N",
//         "ResidentialType": "Residential",
//         "HomeType": "",
//         "MoveInDate": "07/30/2026"
//     },
//     "isEmailOptout": false,
//     "Note": "",
//     "IsSale": false,
//     "IsOptIn": false,
//     "SaleInfo": {
//         "Data": [],
//         "Addons": [
//             "VIVINT NOT APPLICABLE"
//         ],
//         "SessionId": "",
//         "AccountNumber": ""
//     },
//     "Survey": {
//         "Enable": false,
//         "Medium": null,
//         "SurveyNumber": null,
//         "SurveyEmail": null
//     },
//     "ConfirmPhoneNumber": "+12022899898",
//     "ConfirmCallbackNumber": "+12022899898",
//     "IsPGECall": false,
//     "ProvidersCustomerData": null,
//     "TCPA": {
//         "Enable": false,
//         "Number": "+1"
//     },
//     "isVivintPitched": false,
//     "Integration": {
//         "Database": {},
//         "Provider": {}
//     },
//     "OutboundLeadId": "",
//     "PrimaryCallSid": "",
//     "ConferenceSid": "",
//     "ChatTfn": null
// }

// const object2 =  {
//             "_id": "6a6b14f38e0082a77ebcabbb",
//             "call_sid": "TS12026073005100900010",
//             "tfn": "+13652147414",
//             "disposition": {
//                 "name": "Call Back Scheduled "
//             },
//             "customer_info": {
//                 "first_name": "ff",
//                 "last_name": "rr"
//             },
//             "callback_info": {
//                 "time": "2026-07-30T09:20:00Z",
//                 "is_completed": "No"
//             },
//             "CreatedOn": "2026-07-30T09:10:11.773Z",
//             "Duration": 67,
//             "direction": "Inbound",
//             "CallbackNumber": "+12022899898",
//             "Campaign": "Test Skill 1",
//             "CampaignCode": "TS1",
//             "CampaignId": "68650f365ce9346a020fba8a",
//             "TFNCampaign": "Test skill 1",
//             "TFNCampaignCode": "TS1",
//             "TFNCampaignId": "68650f365ce9346a020fba8a",
//             "LanguageCode": null,
//             "ConfirmBTN": "+12022899898",
//             "LeadId": null,
//             "CallSid": "TS12026073005100900010",
//             "Direction": "Inbound",
//             "Disposition": "Call Back Scheduled ",
//             "TFN": "+13652147414"
//         }